import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import FullScreenPostViewer from "../post/FullScreenPostViewer";
import PostFeelingActivity from "../post/PostFeelingActivity";
import "./SavedPosts.css";

// If REACT_APP_API_URL is set to empty string, use relative URLs (same domain)
// Otherwise use the provided URL or default to localhost for development
const API_URL = process.env.REACT_APP_API_URL !== undefined 
  ? process.env.REACT_APP_API_URL 
  : "http://localhost:8000";

export default function SavedPosts({ token }) {
  const navigate = useNavigate();
  const { user } = useSelector((state) => ({ ...state }));
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedPost, setSelectedPost] = useState(null);
  const [loadingPost, setLoadingPost] = useState(false);
  const [postError, setPostError] = useState(null);

  const fetchSavedPosts = useCallback(async () => {
    if (!token) {
      setError("Authentication token is missing. Please log in again.");
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError("");
      const res = await axios.get(`${API_URL}/getSavedPosts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      let postsData = [];
      if (res.data && res.data.posts) {
        postsData = res.data.posts;
      } else if (res.data && res.data.savedPosts) {
        postsData = res.data.savedPosts;
      } else if (Array.isArray(res.data)) {
        postsData = res.data;
      }
      // Archived (and deleted) posts belong only in Archive — never show them in Saved Posts
      postsData = postsData.filter((p) => !p.archived && !p.deleted);
      const sortedPosts = postsData.sort((a, b) => {
        if (a.position !== undefined && b.position !== undefined) return a.position - b.position;
        if (a.order !== undefined && b.order !== undefined) return a.order - b.order;
        return 0;
      });
      setPosts(sortedPosts);
    } catch (err) {
      console.error("Error fetching saved posts:", err);
      setError(err.response?.data?.message || err.message || "Failed to fetch saved posts");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSavedPosts();
  }, [fetchSavedPosts]);

  const openPostInFullScreen = async (post) => {
    if (!post?._id || !user?.token) return;
    setPostError(null);
    setLoadingPost(true);
    try {
      const { data } = await axios.get(`${API_URL}/getPost/${post._id}`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      if (data?.deleted || data?.archived) {
        setPostError("This publication is no longer available (deleted or archived).");
        setPosts((prev) => prev.filter((p) => p._id !== post._id));
        return;
      }
      setSelectedPost(data);
    } catch (err) {
      console.error("Error fetching post:", err);
      const status = err.response?.status;
      const msg = err.response?.data?.message || err.message;
      if (status === 404 || status === 403 || status === 401) {
        setPostError("This publication is no longer available (deleted or private).");
        setPosts((prev) => prev.filter((p) => p._id !== post._id));
      } else {
        setPostError(msg || "Could not load this publication.");
      }
    } finally {
      setLoadingPost(false);
    }
  };

  const handleCloseFullScreen = () => {
    setSelectedPost(null);
    setPostError(null);
  };

  const handleUnsave = useCallback(async (postId) => {
    try {
      await axios.put(`${API_URL}/savePost/${postId}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPosts((prev) => prev.filter((p) => p._id !== postId));
    } catch (err) {
      console.error("Failed to unsave:", err);
      throw err;
    }
  }, [token]);

  const handlePostDeleted = useCallback((postId) => {
    setPosts((prev) => prev.filter((p) => p._id !== postId));
    setSelectedPost(null);
  }, []);

  // When user unsaves from the fullscreen menu, PostMenu already calls the API; just remove from list and close
  const handleRemovedFromSaved = useCallback((postId) => {
    setPosts((prev) => prev.filter((p) => p._id !== postId));
    setSelectedPost(null);
  }, []);

  const handleBack = () => navigate("/");

  return (
    <div className="saved-posts-container">
      <header className="saved-posts-header">
        <button
          onClick={handleBack}
          className="saved-posts-back-button"
          aria-label="Go back"
        >
          ← Back
        </button>
        <h1 className="saved-posts-page-title">Saved items</h1>
      </header>
      {postError && (
        <div
          className="saved-posts-toast"
          role="alert"
          style={{
            position: "fixed",
            top: 20,
            left: "50%",
            transform: "translateX(-50%)",
            padding: "12px 20px",
            background: "#333",
            color: "#fff",
            borderRadius: 8,
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            zIndex: 10001,
            maxWidth: "90%",
            textAlign: "center",
          }}
        >
          {postError}
          <button
            type="button"
            onClick={() => setPostError(null)}
            style={{ marginLeft: 12, background: "transparent", border: "none", color: "#fff", cursor: "pointer", textDecoration: "underline" }}
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="saved-posts-list">
        {loading && <div className="saved-posts-loading">Loading saved publications...</div>}
        {error && <div className="saved-posts-error">{error}</div>}
        {!loading && !error && !posts.length && <div className="saved-posts-empty">No saved publication.</div>}
        {!loading && !error && posts.length > 0 && (
          <>
        {loadingPost && (
          <div className="saved-posts-loading-post" style={{ padding: 24, textAlign: "center", color: "#666" }}>
            Loading publication…
          </div>
        )}
        {posts.map((post) => (
          <div
            key={post._id}
            className="saved-post-item"
            role="button"
            tabIndex={0}
            onClick={() => openPostInFullScreen(post)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openPostInFullScreen(post); } }}
            style={{ cursor: "pointer" }}
          >
            <p className="saved-post-name-label">Saved publication</p>
            <div className="saved-post-meta">
              <span className="saved-post-posted-by">Posted by: {post.username || (post.user && post.user.username) || "Unknown"}</span>
            </div>
            {post.savedAt && (
              <p className="saved-post-saved-at">Saved at: {new Date(post.savedAt).toLocaleString()}</p>
            )}
            {(post.feeling || post.activity) && (
              <div className="saved-post-feeling-activity">
                <PostFeelingActivity
                  feeling={post.feeling}
                  activity={post.activity}
                  userName={post.username || (post.user && post.user.username) || ""}
                />
              </div>
            )}
            {post.images && post.images.length > 0 && post.images.some(img => img.url && !img.url.match(/\.(mp4|webm|ogg)$/)) && (
              <div className="saved-post-media-wrap">
                <img src={post.images.find(img => img.url && !img.url.match(/\.(mp4|webm|ogg)$/)).url} alt="Saved Post" className="saved-post-media" />
              </div>
            )}
            {(
              (post.videos && post.videos.length > 0) ||
              (post.images && post.images.some(img => img.url && img.url.match(/\.(mp4|webm|ogg)$/)))
            ) && (
              <div className="saved-post-media-wrap">
                <video controls controlsList="nodownload noremoteplayback" disablePictureInPicture onContextMenu={(e) => e.preventDefault()} className="saved-post-media" onClick={(e) => e.stopPropagation()}>
                  <source
                    src={
                      post.videos && post.videos.length > 0
                        ? post.videos[0].url
                        : post.images.find(img => img.url && img.url.match(/\.(mp4|webm|ogg)$/)).url
                    }
                    type="video/mp4"
                  />
                  Your browser does not support the video tag.
                </video>
              </div>
            )}
            <div className="saved-post-actions" style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
              {/* <button
                type="button"
                className="saved-post-view-button"
                onClick={(e) => { e.stopPropagation(); openPostInFullScreen(post); }}
                style={{ padding: "8px 16px", borderRadius: 8, background: "#1877f2", color: "#fff", border: "none", cursor: "pointer", fontWeight: 600 }}
              >
                View
              </button> */}
              <button
                className="saved-post-unsave-button"
                title="Unsave Post"
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    await handleUnsave(post._id);
                  } catch (err) {
                    alert("Failed to unsave post");
                  }
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#e74c3c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
                  <line x1="4" y1="4" x2="20" y2="20" />
                </svg>
              </button>
            </div>
          </div>
        ))}
          </>
        )}
      </div>

      {selectedPost && user && (
        <FullScreenPostViewer
          post={selectedPost}
          user={user}
          onClose={handleCloseFullScreen}
          onPostDeleted={handlePostDeleted}
          isSavedView
          onUnsave={handleRemovedFromSaved}
        />
      )}
    </div>
  );
}
